import { create } from 'zustand';
import type { AiChatMessage } from '@flows.cash/types';
import { sendChatMessage, CHAT_HISTORY_LIMIT } from '../services/aiChatService';
import type { AiChatErrorCode } from '@flows.cash/types';

interface AiChatState {
  messages: AiChatMessage[];
  isSending: boolean;
  error: AiChatErrorCode | null;
  send: (message: string, locale?: string) => Promise<void>;
  clear: () => void;
}

// Ask-your-money chat state, shared by the web page and the mobile screen.
// History lives in memory only (survives navigation, clears on app restart);
// the server stores nothing, and each turn resends the recent history.
export const useAiChatStore = create<AiChatState>((set, get) => ({
  messages: [],
  isSending: false,
  error: null,

  send: async (message: string, locale = 'en') => {
    const trimmed = message.trim();
    if (!trimmed || get().isSending) return;

    // History as it was BEFORE this user message.
    const history = get().messages.slice(-CHAT_HISTORY_LIMIT);
    set((state) => ({
      messages: [...state.messages, { role: 'user', content: trimmed }],
      isSending: true,
      error: null,
    }));

    const { data, error } = await sendChatMessage(trimmed, history, locale);

    if (error) {
      set({ isSending: false, error: error.error });
      return;
    }

    set((state) => ({
      messages: [...state.messages, { role: 'assistant', content: data.reply }],
      isSending: false,
    }));
  },

  clear: () => set({ messages: [], error: null }),
}));
